import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, take } from 'rxjs/operators';
import { Alert } from 'src/app/models/alert';
import { AlertDetailService } from 'src/app/diagnostics/component/alert-detail/alert-detail.service';
import { TenantFeatures, isFeatureEnabled } from 'src/app/models/featureSettings';
import { TenantSessionState } from 'src/app/store/state/tenant-session.state';
import { ALERT_QUICK_VIEW_DIALOG_CONFIG, AlertQuickViewDialogComponent } from '../alert-quick-view-dialog/alert-quick-view-dialog.component';
import { ALERT_TRIAGE_DIALOG_CONFIG, AlertDetailDialogComponent, AlertDetailDialogData } from './alert-detail-dialog.component';
import { alertIdFromLocation, fetchLinkedAlert, removeAlertParam } from './alert-link';
import { AlertDialogResult, AlertTriageOpener } from './alert-triage-opener';

@Injectable()
export class AlertTriageDialogService extends AlertTriageOpener {
  readonly linkedAlertId$: Observable<string>;
  private ref: MatDialogRef<AlertDetailDialogComponent | AlertQuickViewDialogComponent, AlertDialogResult> | null = null;

  constructor(private dialog: MatDialog, private router: Router, private alertDetailService: AlertDetailService, private store: Store) {
    super();
    this.linkedAlertId$ = router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      startWith(null),
      map(() => alertIdFromLocation()),
      distinctUntilChanged(),
    );
  }

  open(data: AlertDetailDialogData): Observable<AlertDialogResult | undefined> {
    this.ref?.close();
    const v2 = isFeatureEnabled(this.store.selectSnapshot(TenantSessionState.getTenantFeatures), TenantFeatures.AlertDialogV2);
    const ref: MatDialogRef<AlertDetailDialogComponent | AlertQuickViewDialogComponent, AlertDialogResult> = v2
      ? this.dialog.open(AlertDetailDialogComponent, { ...ALERT_TRIAGE_DIALOG_CONFIG, data })
      : this.dialog.open(AlertQuickViewDialogComponent, { ...ALERT_QUICK_VIEW_DIALOG_CONFIG, data });
    this.ref = ref;
    ref.afterClosed().pipe(take(1)).subscribe(() => {
      if (this.ref !== ref) return;
      this.ref = null;
      if (!v2 && alertIdFromLocation()) removeAlertParam(this.router);
    });
    return ref.afterClosed();
  }

  show(data: AlertDetailDialogData): void {
    const open = this.ref?.componentInstance;
    if (open instanceof AlertDetailDialogComponent) open.show(data.alert);
    else this.open(data);
  }

  openLinked<T>(find: (id: string) => T | undefined, fromAlert: (alert: Alert) => T, open: (seed: T) => void): void {
    const id = alertIdFromLocation();
    if (!id || this.showing(id)) return;
    const seed = find(id);
    if (seed !== undefined) {
      open(seed);
      return;
    }
    fetchLinkedAlert(this.alertDetailService, id).subscribe(alert => {
      if (alertIdFromLocation() !== id || this.showing(id)) return;
      if (alert) open(fromAlert(alert));
      else removeAlertParam(this.router);
    });
  }

  private showing(id: string): boolean {
    return this.ref?.componentInstance?.current?.latestAlertId === id;
  }
}
